import React, { useState, useEffect } from 'react';
import { useCalendars } from '../../contexts/CalendarContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { lookupUserByEmail, getUserProfile } from '../../services/userService';
import Button from '../Common/Button';
import Input from '../Common/Input';

const CalendarShareModal = ({ isOpen, onClose, calendar }) => {
    const { shareCalendar, updateMemberPermission, removeMember, regenerateCode, PERMISSIONS } = useCalendars();
    const { currentUser } = useAuth();
    const { createNotification } = useNotifications();

    const [email, setEmail] = useState('');
    const [selectedPermission, setSelectedPermission] = useState(PERMISSIONS.VIEWER);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [members, setMembers] = useState([]);
    const [shareCode, setShareCode] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    const loadMembers = async () => {
        if (!calendar) return;

        // Set share code
        setShareCode(calendar.shareCode || '');

        // Get member details from calendar with user profile info
        const memberPromises = calendar.members.map(async (memberId) => {
            try {
                const profile = await getUserProfile(memberId);
                return {
                    id: memberId,
                    email: profile?.email || 'Unknown',
                    displayName: profile?.displayName || null,
                    permission: calendar.permissions[memberId],
                    isOwner: memberId === calendar.ownerId,
                    isCurrentUser: memberId === currentUser?.uid
                };
            } catch (error) {
                // If profile not found, return basic info
                return {
                    id: memberId,
                    email: 'Unknown',
                    displayName: null,
                    permission: calendar.permissions[memberId],
                    isOwner: memberId === calendar.ownerId,
                    isCurrentUser: memberId === currentUser?.uid
                };
            }
        });

        const memberList = await Promise.all(memberPromises);
        setMembers(memberList.filter(member => !member.isCurrentUser));
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            loadMembers();
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, calendar]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleClose = () => {
        setEmail('');
        setSelectedPermission(PERMISSIONS.VIEWER);
        setError('');
        setSuccess('');
        setCopySuccess(false);
        onClose();
    };

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(shareCode);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            setError('Failed to copy code to clipboard');
        }
    };

    const handleRegenerateCode = async () => {
        if (!window.confirm('Are you sure you want to regenerate the share code? The old code will no longer work.')) {
            return;
        }

        setIsRegenerating(true);
        setError('');
        setSuccess('');

        try {
            const newCode = await regenerateCode(calendar.id);
            setShareCode(newCode);
            setSuccess('Share code regenerated successfully!');
        } catch (err) {
            setError(err.message || 'Failed to regenerate share code');
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleInviteByEmail = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setError('Please enter an email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            // Look up user by email
            const targetUser = await lookupUserByEmail(email.trim());

            if (!targetUser) {
                setError('No user found with that email address');
                return;
            }

            // Check if user is already a member
            if (calendar.members.includes(targetUser.id)) {
                setError('This user is already a member of this calendar');
                return;
            }

            // Share calendar with found user
            await shareCalendar(calendar.id, targetUser.id, selectedPermission);

            // Send notification to invited user
            await createNotification(
                targetUser.id,
                'calendar-invite',
                'Calendar Invitation',
                `${currentUser?.email || 'Someone'} invited you to collaborate on "${calendar.name}"`,
                {
                    calendarId: calendar.id,
                    calendarName: calendar.name,
                    invitedBy: currentUser?.email,
                    permission: selectedPermission
                }
            );

            setSuccess(`Calendar shared with ${email} successfully!`);
            setEmail('');
            await loadMembers();
        } catch (err) {
            setError(err.message || 'Failed to share calendar');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChangePermission = async (memberId, newPermission) => {
        setError('');
        setSuccess('');

        try {
            await updateMemberPermission(calendar.id, memberId, newPermission);
            setSuccess('Permission updated successfully');
            loadMembers();
        } catch (err) {
            setError(err.message || 'Failed to update permission');
        }
    };

    const handleRemoveMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to remove this member from the calendar?')) {
            return;
        }

        setError('');
        setSuccess('');

        try {
            await removeMember(calendar.id, memberId);
            setSuccess('Member removed successfully');
            loadMembers();
        } catch (err) {
            setError(err.message || 'Failed to remove member');
        }
    };

    const getPermissionBadgeColor = (permission) => {
        switch (permission) {
            case PERMISSIONS.OWNER:
                return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200';
            case PERMISSIONS.EDITOR:
                return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
            case PERMISSIONS.VIEWER:
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
            default:
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
        }
    };

    if (!isOpen || !calendar) return null;

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-modal-title"
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 animate-fade-in transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Header */}
                    <div className="mb-6">
                        <h2 id="share-modal-title" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Share Calendar
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Share "{calendar.name}" with other users
                        </p>
                    </div>

                    {/* Error/Success Messages */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                        </div>
                    )}

                    {/* Share Code Section */}
                    <div className="mb-6 p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-2 border-primary-200 dark:border-primary-700 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Share Code
                            </h3>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Secure
                            </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Share this code with others to let them join. Code regenerates automatically when you log in for privacy.
                        </p>

                        <div className="flex items-center gap-3">
                            <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border-2 border-gray-300 dark:border-gray-600">
                                <code className="text-2xl font-mono font-bold text-primary-600 dark:text-primary-400 tracking-wider">
                                    {shareCode || 'Loading...'}
                                </code>
                            </div>

                            <button
                                onClick={handleCopyCode}
                                className="px-4 py-3 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
                                title="Copy share code"
                            >
                                {copySuccess ? (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Copy
                                    </>
                                )}
                            </button>
                        </div>

                        {calendar.ownerId === currentUser?.uid && (
                            <div className="mt-3 flex justify-between items-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Auto-regenerates on login
                                </p>
                                <button
                                    onClick={handleRegenerateCode}
                                    disabled={isRegenerating}
                                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline disabled:opacity-50"
                                >
                                    {isRegenerating ? 'Regenerating...' : 'Regenerate Now'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Invite Form */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Invite User
                        </h3>

                        <form onSubmit={handleInviteByEmail} className="space-y-4">
                            <div>
                                <Input
                                    label="Email Address"
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter user's email address"
                                    disabled={isSubmitting}
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    The user must have an account to be invited.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Permission Level
                                </label>
                                <select
                                    value={selectedPermission}
                                    onChange={(e) => setSelectedPermission(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    disabled={isSubmitting}
                                >
                                    <option value={PERMISSIONS.VIEWER}>Viewer - Can only view tasks</option>
                                    <option value={PERMISSIONS.EDITOR}>Editor - Can create and edit tasks</option>
                                </select>
                            </div>

                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                                className="w-full"
                            >
                                {isSubmitting ? 'Inviting...' : 'Invite User'}
                            </Button>
                        </form>
                    </div>

                    {/* Current Members */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Current Members ({members.length + 1})
                        </h3>

                        {/* Show current user */}
                        <div className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        You
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {currentUser?.email || 'Current user'}
                                    </p>
                                </div>
                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getPermissionBadgeColor(calendar.permissions[currentUser?.uid])}`}>
                                    {calendar.permissions[currentUser?.uid] || 'Member'}
                                </span>
                            </div>
                        </div>

                        {/* Show other members */}
                        {members.length > 0 ? (
                            <div className="space-y-3">
                                {members.map((member) => (
                                    <div
                                        key={member.id}
                                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                                    {member.displayName || member.email}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                    {member.displayName ? member.email : (member.isOwner ? 'Calendar Owner' : '')}
                                                </p>
                                            </div>

                                            <div className="flex items-center space-x-3 ml-4">
                                                {!member.isOwner && (
                                                    <>
                                                        {/* Permission Selector */}
                                                        <select
                                                            value={member.permission}
                                                            onChange={(e) => handleChangePermission(member.id, e.target.value)}
                                                            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                        >
                                                            <option value={PERMISSIONS.VIEWER}>Viewer</option>
                                                            <option value={PERMISSIONS.EDITOR}>Editor</option>
                                                        </select>

                                                        {/* Remove Button */}
                                                        <button
                                                            onClick={() => handleRemoveMember(member.id)}
                                                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                            aria-label="Remove member"
                                                            title="Remove member"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}

                                                {member.isOwner && (
                                                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getPermissionBadgeColor(PERMISSIONS.OWNER)}`}>
                                                        Owner
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                                No other members yet. Invite someone to collaborate!
                            </p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="mt-6 flex justify-end">
                        <Button variant="ghost" onClick={handleClose}>
                            Done
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarShareModal;
